'use client';

// Live team-activity toasts. A colleague signing a placement, adding an
// interviewproces, or firing off CV sends surfaces as a small HV2 card top-right.
//
// The events are NOT fetched here: they piggyback the existing /api/deals poll
// (app/page.js -> ingestTeamEvents), so this component adds ZERO network cost. It
// only decides what to SHOW: it filters out the viewer's own actions, dedupes
// already-seen events (in-memory + sessionStorage so a refresh never replays), and
// stacks at most 3 cards with a per-type visible duration. Click opens the record.
//
// HV2 house style (owner rules): compact chip (bgSub, 0.5px border, borderRadius 5),
// kobalt stroke icon per type. NO emoji, NO amber, NO black buttons, and REGEL 21:
// the chip is a single line - no explanatory body text.

import React, { useState, useEffect, useCallback } from 'react';
import { HV2, t, modalRadius, BRANCH_PATHS } from './lib';
import { CV_SEND_PATHS } from '@/lib/kpi-icons.mjs';

// ── Imperative feed (mirrors Toast.js's pub/sub so the poll can push from anywhere) ──
const listeners = new Set();
// Push a batch of teamEvents plus the viewer's own id (own actions are dropped here
// so the filter always uses the freshest identity). Called from refreshDeals.
export function ingestTeamEvents(events, currentUserId) {
  if (!Array.isArray(events) || !events.length) return;
  listeners.forEach((fn) => fn(events, currentUserId != null ? String(currentUserId) : ''));
}

// Per-type visible duration (ms) - straight from the owner spec.
const DURATION = {
  cv_send: 7000,
  int_proces: 10000,
  prospect_signed: 10000,
  placement_signed: 60000,
  retained_sold: 60000,
};

// Thin stroke icon per type (inherit stroke=kobalt). No emoji (owner rule).
const ICON = {
  cv_send: <>{CV_SEND_PATHS.map((d, i) => <path key={i} d={d} />)}</>, // CV Send: document met pijl
  int_proces: <>{BRANCH_PATHS.map((d, i) => <path key={i} d={d} />)}</>, // proces
  prospect_signed: <><path d="M20 6 9 17l-5-5" /></>, // check
  placement_signed: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="M22 4 12 14.01l-3-3" /></>, // check-circle
  retained_sold: <><path d="M12 2 15.09 8.26 22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14l-5-4.87 6.91-1.01Z" /></>, // star (retained = premium)
};

// Compose the toast sentence from the event. Dutch source + {placeholders}; the EN
// keys live at the bottom of i18n.js so t() localises when the toggle is unlocked.
function messageFor(e) {
  const naam = e.userName || t('Een collega');
  switch (e.type) {
    case 'cv_send':
      return e.count > 1
        ? t('{naam} deed {n} CV sends', { naam, n: e.count })
        : t('{naam} deed een CV send', { naam });
    case 'int_proces':
      return t('{naam} voegde een interviewproces toe', { naam });
    case 'prospect_signed':
      return t('{naam} tekende {bedrijf} als klant', { naam, bedrijf: e.label || t('een prospect') });
    case 'placement_signed':
      return t('{naam}: plaatsing getekend - {kandidaat}', { naam, kandidaat: e.label || '' });
    case 'retained_sold':
      return t('{naam}: retained search verkocht', { naam });
    default:
      return naam;
  }
}

// Seen-set: survives a refresh (sessionStorage) so an event never replays, but is
// per-tab-session so it does eventually reset. Bounded so it can't grow unbounded.
const SEEN_KEY = 'pb-team-toasts-seen';
function loadSeen() {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}
function persistSeen(seen) {
  try {
    const arr = [...seen].slice(-300); // keep the most recent ids only
    sessionStorage.setItem(SEEN_KEY, JSON.stringify(arr));
  } catch { /* private mode / quota - in-memory dedupe still holds this session */ }
}

function openRecord(e) {
  if (!e.dealId || typeof window === 'undefined') return;
  // hashNav: the app's popstate/hash handler opens #proces-<id> (app/page.js).
  window.location.hash = `#proces-${encodeURIComponent(e.dealId)}`;
}

function openRecordInNewTab(e) {
  if (!e.dealId || typeof window === 'undefined') return;
  window.open(`/#proces-${encodeURIComponent(e.dealId)}`, '_blank', 'noopener,noreferrer');
}

// Phone breakpoint, matches the app-wide isMobile threshold (innerWidth < 768).
// This component mounts standalone in the layout (no isMobile prop reaches it),
// so it tracks the media query itself. SSR-safe: state starts false (desktop),
// the real value lands after mount.
function useIsMobileViewport() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    setIsMobile(mql.matches);
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

function ToastCard({ e, onClose, isMobile }) {
  const clickable = !!e.dealId;
  const dur = DURATION[e.type] || 10000;
  return (
    <div
      role="status"
      onAuxClick={clickable ? (ev) => { if (ev.button !== 1) return; ev.preventDefault(); openRecordInNewTab(e); onClose(); } : undefined}
      onClick={clickable ? (ev) => { if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) openRecordInNewTab(e); else openRecord(e); onClose(); } : undefined}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7, maxWidth: isMobile ? 'none' : '92vw', width: isMobile ? '100%' : 'auto', boxSizing: 'border-box',
        padding: '6px 10px', borderRadius: modalRadius, background: HV2.bgSub,
        border: `0.5px solid ${HV2.border}`, pointerEvents: 'auto',
        position: 'relative', cursor: clickable ? 'pointer' : 'default',
        animation: 'pa-tt-in 0.2s ease-out',
      }}
    >
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={HV2.blue} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>{ICON[e.type] || ICON.int_proces}</svg>
      <span style={{ fontSize: HV2.fsText, fontWeight: 480, color: HV2.ink2, whiteSpace: isMobile ? 'normal' : 'nowrap' }}>{messageFor(e)}</span>
      <button
        onClick={(ev) => { ev.stopPropagation(); onClose(); }}
        aria-label={t('Sluiten')}
        style={{
          border: 'none', background: 'transparent', cursor: 'pointer', color: HV2.mute2, fontSize: 14, lineHeight: 1, padding: '0 2px', flexShrink: 0, marginLeft: 4,
          // Telefoon: 32x32 tikgebied zonder de kaart hoger te maken dan nu (negatieve
          // marge trekt het omhoog/omlaag uitgerekte gebied weer in).
          ...(isMobile ? { minWidth: 32, minHeight: 32, marginTop: -7, marginBottom: -7, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' } : null),
        }}
      >×</button>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, borderRadius: '0 0 5px 5px', overflow: 'hidden' }}>
        <div style={{ height: 2, background: HV2.borderStrong, animation: `pa-tt-prog ${dur}ms linear forwards` }} />
      </div>
    </div>
  );
}

export default function TeamToastStack() {
  const [items, setItems] = useState([]);
  const isMobile = useIsMobileViewport();
  // Meet de eigen stapelhoogte en publiceer die als CSS-variabele, zodat
  // VersionWatcher er op de telefoon boven kan stapelen in plaats van op een
  // vast getal dat overlapt zodra een kaart twee regels wordt. Het element
  // wordt in state bijgehouden (niet een kale ref) zodat de observer in een
  // effect leeft: die combinatie overleeft React's dubbele effect-ronde, een
  // observer die alleen in de ref-callback ontstaat niet.
  const [wrapEl, setWrapEl] = useState(null);
  const wrapRef = useCallback((el) => setWrapEl(el || null), []);
  useEffect(() => {
    const set = (px) => document.documentElement.style.setProperty('--pa-teamtoast-h', px);
    if (!isMobile || !wrapEl || typeof window === 'undefined' || typeof window.ResizeObserver !== 'function') {
      set('0px');
      return undefined;
    }
    const ro = new window.ResizeObserver((entries) => {
      const h = entries && entries[0] && entries[0].contentRect ? entries[0].contentRect.height : 0;
      set(`${h + 6}px`);
    });
    ro.observe(wrapEl);
    return () => { ro.disconnect(); set('0px'); };
  }, [isMobile, wrapEl]);
  useEffect(() => {
    const seen = loadSeen();
    const onEvents = (events, currentUserId) => {
      const fresh = [];
      for (const e of events) {
        if (!e || !e.id) continue;
        if (currentUserId && String(e.userId) === currentUserId) continue; // never toast own actions
        if (seen.has(e.id)) continue;
        seen.add(e.id);
        fresh.push(e);
      }
      if (!fresh.length) return;
      persistSeen(seen);
      // Oldest-first into the stack so the newest ends up on top; cap at 3 (older slide away).
      fresh.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
      setItems((prev) => [...prev, ...fresh].slice(-3));
      for (const e of fresh) {
        const dur = DURATION[e.type] || 10000;
        setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== e.id)), dur);
      }
    };
    listeners.add(onEvents);
    return () => listeners.delete(onEvents);
  }, []);

  const dismiss = (id) => setItems((prev) => prev.filter((x) => x.id !== id));
  if (!items.length) return null;
  // Phone: docked to the bottom, stacked above the imperative <Toaster/> (which
  // sits closest to the edge at bottom 12) so the two never overlap. Desktop
  // keeps the original top-60 dock, distinct from Toaster's top-16.
  const wrapStyle = isMobile
    ? { position: 'fixed', left: 12, right: 12, bottom: 'calc(12px + var(--pa-toaster-h, 0px) + env(safe-area-inset-bottom))', zIndex: 9990, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 6, maxHeight: 'calc(100vh - 96px)', overflow: 'hidden', pointerEvents: 'none' }
    : { position: 'fixed', top: 60, right: 16, zIndex: 9990, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, maxHeight: 'calc(100vh - 76px)', overflow: 'hidden', pointerEvents: 'none' };
  return (
    <div ref={wrapRef} style={wrapStyle}>
      {items.map((e) => <ToastCard key={e.id} e={e} onClose={() => dismiss(e.id)} isMobile={isMobile} />)}
      <style>{'@keyframes pa-tt-in{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}@keyframes pa-tt-prog{from{width:100%}to{width:0%}}'}</style>
    </div>
  );
}
